import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { formatDate } from 'commons/utils/date'
import { toast } from 'react-toastify'
import {
  getCategoryReportData,
  getDashboardData,
  getGeneralMetaByRegionData,
  getGeneralMetaByTeamData,
  getGeneralMetaReportData,
  getHomeQuotaMonths,
  getMetaReportData,
  getOfficeGroupsData,
  getRankingData,
  getRankingOfficesData,
  getRankingReportData,
} from 'services/home'
import type {
  DashboardRequestBody,
  DashboardResponse,
  GeneralMetaByRegionRequestBody,
  GeneralMetaByTeamRequestBody,
  MetaResultTeam,
  OfficeGroup,
  queryPagination,
  RankingItem,
  RankingOffice,
  RankingRequestBody,
} from 'services/home/interfaces'
import type { UserPreferences } from 'services/login/interfaces'
import { getCommercialStructureData } from 'services/shared'
import type { Pagination } from 'store/InventoryContext/Types/InventoryType'
import type { AppThunk } from 'store/store'

type InitialStateType = {
  loadingOptions: boolean
  loadingDashboard: boolean
  loadingMetaReport: boolean
  loadingCategoryReport: boolean
  loadingRankingReport: boolean
  loadingRanking: boolean
  loadingTableItem: boolean
  loadingQuotaOptions: boolean
  quotaOptions: string[]
  selectedQuota: string
  dashboardData: DashboardResponse
  rankingOffices: RankingOffice[]
  rankingData: RankingItem[]
  generalMetaData: MetaResultTeam[]
  rankingUpdateDate: string
  rankingTotalVolume: number
  rankingPag: Pagination
  rankingQuota: string
  rankingQuotaOptions: string[]
  officeGroups: OfficeGroup[]
}

const initialState: InitialStateType = {
  dashboardData: {
    CarteiraTotal: {
      VolumeAprovado: 0,
      VolumeDigitado: 0,
      VolumeRestante: 0,
      VolumeTotal: 0,
    },
    CestasDeProdutos: [],
    ProdutosEstrategicos: [],
  },
  generalMetaData: [],
  loadingCategoryReport: false,
  loadingDashboard: true,
  loadingMetaReport: false,
  loadingOptions: false,
  loadingQuotaOptions: false,
  loadingRanking: false,
  loadingRankingReport: false,
  loadingTableItem: false,
  officeGroups: [],
  quotaOptions: [],
  rankingData: [],
  rankingOffices: [],
  rankingPag: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  rankingQuota: '',
  rankingQuotaOptions: [],
  rankingTotalVolume: 0,
  rankingUpdateDate: '',
  selectedQuota: '',
}

const home = createSlice({
  initialState,
  name: 'home',
  reducers: {
    resetRankingPagination(state) {
      state.rankingPag = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      }
    },
    setDashboardData(state, action: PayloadAction<DashboardResponse>) {
      state.dashboardData = action.payload
    },
    setGeneralMetaData(state, action: PayloadAction<MetaResultTeam[]>) {
      state.generalMetaData = action.payload
    },
    setLoadingCategoryReport(state, action: PayloadAction<boolean>) {
      state.loadingCategoryReport = action.payload
    },
    setLoadingDash(state, action: PayloadAction<boolean>) {
      state.loadingDashboard = action.payload
    },
    setLoadingMetaReport(state, action: PayloadAction<boolean>) {
      state.loadingMetaReport = action.payload
    },
    setLoadingOptions(state, action: PayloadAction<boolean>) {
      state.loadingOptions = action.payload
    },
    setLoadingQuotaOptions(state, action: PayloadAction<boolean>) {
      state.loadingQuotaOptions = action.payload
    },
    setLoadingRanking(state, action: PayloadAction<boolean>) {
      state.loadingRanking = action.payload
    },
    setLoadingRankingReport(state, action: PayloadAction<boolean>) {
      state.loadingRankingReport = action.payload
    },
    setLoadingTableItem(state, action: PayloadAction<boolean>) {
      state.loadingTableItem = action.payload
    },
    setOfficeGroups(state, action: PayloadAction<OfficeGroup[]>) {
      state.officeGroups = action.payload
    },
    setQuotaOptions(state, action: PayloadAction<string[]>) {
      state.quotaOptions = action.payload
    },
    setRankingData(state, action: PayloadAction<RankingItem[]>) {
      state.rankingData = action.payload
    },
    setRankingOffices(state, action: PayloadAction<RankingOffice[]>) {
      state.rankingOffices = action.payload
    },
    setRankingPage(state, action: PayloadAction<number>) {
      state.rankingPag.currentPage = action.payload
    },
    setRankingPagination(state, action: PayloadAction<Pagination>) {
      state.rankingPag = action.payload
    },
    setRankingQuota(state, action: PayloadAction<string>) {
      state.rankingQuota = action.payload
    },
    setRankingQuotaOptions(state, action: PayloadAction<string[]>) {
      state.rankingQuotaOptions = action.payload
    },
    setRankingTotalVolume(state, action: PayloadAction<number>) {
      state.rankingTotalVolume = action.payload
    },
    setRankingUpdateDate(state, action: PayloadAction<string>) {
      state.rankingUpdateDate = action.payload
    },
    setSelectedQuota(state, action: PayloadAction<string>) {
      state.selectedQuota = action.payload
    },
  },
})

export const {
  setLoadingOptions,
  setLoadingDash,
  setLoadingMetaReport,
  setLoadingCategoryReport,
  setLoadingRankingReport,
  setLoadingRanking,
  setLoadingTableItem,
  setLoadingQuotaOptions,
  setQuotaOptions,
  setSelectedQuota,
  setDashboardData,
  setRankingOffices,
  setRankingData,
  setGeneralMetaData,
  setRankingPage,
  setRankingUpdateDate,
  setRankingTotalVolume,
  setRankingPagination,
  resetRankingPagination,
  setRankingQuota,
  setRankingQuotaOptions,
  setOfficeGroups,
} = home.actions

export const homeContext = home.reducer

export const getFilterOptions = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingOptions(true))
  dispatch(setLoadingDash(true))

  try {
    const preferences = JSON.parse(localStorage.getItem('preferences') || '{}') as UserPreferences
    const {
      RegiaoVenda: prefZone,
      EquipeVenda: prefRegion,
      EscritorioVenda: prefOffice,
    } = preferences

    const offices = await getCommercialStructureData()

    if (offices.length) {
      const officeCodes = offices.map((item) => item.CodigoEscritorio)
      const selectedOffices = prefOffice ? [prefOffice] : officeCodes
      const quotas = await getHomeQuotaMonths(selectedOffices)
      if (quotas.length) {
        const quotaTitles = quotas.map((item) => formatDate(item.MesCota, 'MM/YY', 'MM/YYYY'))

        dispatch(setQuotaOptions(quotaTitles))
        dispatch(setSelectedQuota(quotaTitles[0]))

        const formattedQuota = formatDate(quotaTitles[0], 'YYYYMM', 'MM/YY')
        dispatch(
          getDashboard({
            AnoMesCota: formattedQuota,
            EscritorioVenda: prefOffice ? [prefOffice] : officeCodes,
            Regional: prefRegion ? [prefRegion] : [],
            Zona: prefZone ? [prefZone] : [],
          }),
        )
      }
    }
  } catch (error) {
    toast.error('Erro ao carregar gestão de carteira.')
  } finally {
    dispatch(setLoadingOptions(false))
  }
}

export const getQuotaMonthOptions =
  (offices: string[]): AppThunk =>
  async (dispatch, state) => {
    dispatch(setLoadingQuotaOptions(true))
    try {
      const quotas = await getHomeQuotaMonths(offices)
      if (quotas.length) {
        const quotaTitles = quotas.map((item) => formatDate(item.MesCota, 'MM/YY', 'MM/YYYY'))
        const selectedQuota = state().home.selectedQuota
        if (!quotaTitles.includes(selectedQuota)) {
          dispatch(setSelectedQuota(quotaTitles[0]))
        }
        dispatch(setQuotaOptions(quotaTitles))
      }
    } catch (error) {
      toast.error('Erro ao carregar opções de mês cota.')
    } finally {
      dispatch(setLoadingQuotaOptions(false))
    }
  }

export const getDashboard =
  (params: DashboardRequestBody): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingDash(true))
    try {
      const data = await getDashboardData(params)
      dispatch(setDashboardData(data))
    } catch (error) {
      toast.error('Erro ao carregar gestão de carteira.')
    } finally {
      dispatch(setLoadingDash(false))
    }
  }

export const getMetaOptions = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingRanking(true))
  try {
    const offices = await getRankingOfficesData()
    if (offices.length) {
      dispatch(setRankingOffices(offices))
    }
    const quotas = await getHomeQuotaMonths(offices.map((item) => item.cd_escr_vend))
    if (quotas.length) {
      const quotaTitles = quotas.map((item) => formatDate(item.MesCota, 'MM/YY', 'MM/YYYY'))
      dispatch(setRankingQuotaOptions(quotaTitles))
      dispatch(setRankingQuota(quotaTitles[0]))
      dispatch(
        getRanking(
          {
            AnoMesCota: quotas[0].Descricao,
            Escritorio: null,
            Gerente: null,
            Regional: null,
          },
          { linhas: 10, paginas: 0 },
        ),
      )
    }
  } catch (error) {
    toast.error('Erro ao carregar opções de meta.')
  }
}

export const getRanking =
  (body: RankingRequestBody, pagination: queryPagination): AppThunk =>
  async (dispatch, state) => {
    dispatch(setLoadingRanking(true))
    try {
      if (body.Escritorio) {
        const groups = await getOfficeGroupsData(body.Escritorio, body.AnoMesCota)
        if (groups.length) {
          dispatch(setOfficeGroups(groups))
        } else {
          dispatch(setOfficeGroups([]))
        }
      }
      let quota = body.AnoMesCota
      const offices = state().home.rankingOffices.map((item) => item.cd_escr_vend)
      const quotas = await getHomeQuotaMonths(body.Escritorio ? [body.Escritorio] : offices)
      if (quotas.length) {
        const quotaTitles = quotas.map((item) => formatDate(item.MesCota, 'MM/YY', 'MM/YYYY'))
        const selectedQuota = state().home.rankingQuota
        if (!quotaTitles.includes(selectedQuota)) {
          quota = quotas[0].Descricao
          dispatch(setRankingQuota(quotaTitles[0]))
        }
        dispatch(setRankingQuotaOptions(quotaTitles))
      }

      const data = await getRankingData({ ...body, AnoMesCota: quota }, pagination)
      if (data.RankingMetaResponse?.length) {
        dispatch(setRankingTotalVolume(data.TotalVolumeAprovado))
        dispatch(setRankingData(data.RankingMetaResponse))
        dispatch(setRankingUpdateDate(formatDate(new Date(), 'DD/MM/YYYY HH:mm:ss')))
        dispatch(
          setRankingPagination({
            currentPage: pagination.paginas + 1,
            pageSize: pagination.linhas,
            totalItems: data.RowsCount,
            totalPages: Math.ceil(data.RowsCount / pagination.linhas),
          }),
        )
      } else {
        dispatch(setRankingTotalVolume(0))
        dispatch(setRankingData([]))
        dispatch(resetRankingPagination())
      }
    } catch (error) {
      toast.error('Erro ao carregar ranking de metas.')
      dispatch(setRankingTotalVolume(0))
      dispatch(setRankingData([]))
      dispatch(resetRankingPagination())
    } finally {
      dispatch(setLoadingRanking(false))
    }
  }

export const getGeneralMeta =
  (body: GeneralMetaByTeamRequestBody): AppThunk =>
  async (dispatch, state) => {
    dispatch(setLoadingRanking(true))
    try {
      let quota = body.AnoMesCota
      const quotas = await getHomeQuotaMonths([body.Escritorio])
      if (quotas.length) {
        const quotaTitles = quotas.map((item) => formatDate(item.MesCota, 'MM/YY', 'MM/YYYY'))
        const selectedQuota = state().home.rankingQuota
        if (!quotaTitles.includes(selectedQuota)) {
          quota = quotas[0].Descricao
          dispatch(setRankingQuota(quotaTitles[0]))
        }
        dispatch(setRankingQuotaOptions(quotaTitles))
      }

      const data = await getGeneralMetaByTeamData({ ...body, AnoMesCota: quota })
      if (data.Equipes?.length) {
        dispatch(setRankingTotalVolume(data.MetaTotal))
        dispatch(setGeneralMetaData(data.Equipes))
        dispatch(setRankingUpdateDate(formatDate(new Date(), 'DD/MM/YYYY HH:mm:ss')))
      } else {
        dispatch(setRankingTotalVolume(0))
        dispatch(setGeneralMetaData([]))
      }
    } catch (error) {
      toast.error('Erro ao carregar meta geral.')
      dispatch(setRankingTotalVolume(0))
      dispatch(setGeneralMetaData([]))
    } finally {
      dispatch(setLoadingRanking(false))
    }
  }

export const getMetaReport =
  (
    params: DashboardRequestBody,
    regionOptionsLength: number,
    zoneOptionsLength: number,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingMetaReport(true))
    toast.info('O download iniciará em instantes.')
    if (params.Regional.length === regionOptionsLength) {
      params.Regional = []
    }
    if (params.Zona.length === zoneOptionsLength) {
      params.Zona = []
    }
    try {
      const data = await getMetaReportData(params)
      const url = URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'meta-venda.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Arquivo baixado com sucesso!')
    } catch (error) {
      toast.error('Erro ao obter dados.')
    } finally {
      dispatch(setLoadingMetaReport(false))
    }
  }

export const getCategoryReport =
  (
    params: DashboardRequestBody,
    regionOptionsLength: number,
    zoneOptionsLength: number,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingCategoryReport(true))
    toast.info('O download iniciará em instantes.')
    if (params.Regional.length === regionOptionsLength) {
      params.Regional = []
    }
    if (params.Zona.length === zoneOptionsLength) {
      params.Zona = []
    }
    try {
      const data = await getCategoryReportData(params)
      const url = URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'cesta-produto.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Arquivo baixado com sucesso!')
    } catch (error) {
      toast.error('Erro ao obter dados.')
    } finally {
      dispatch(setLoadingCategoryReport(false))
    }
  }

export const getRankingReport =
  (params: RankingRequestBody): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingRankingReport(true))
    toast.info('O download iniciará em instantes.')
    try {
      const data = await getRankingReportData(params)
      const url = URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      const fileOffice = params.Escritorio ?? 'todos'
      link.setAttribute('download', `ranking-meta-${fileOffice}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Arquivo baixado com sucesso!')
    } catch (error) {
      toast.error('Erro ao obter relatório.')
    } finally {
      dispatch(setLoadingRankingReport(false))
    }
  }

export const getGeneralMetaItem =
  (id: string, body: GeneralMetaByRegionRequestBody): AppThunk =>
  async (dispatch, state) => {
    dispatch(setLoadingTableItem(true))
    try {
      const generalMetaData = state().home.generalMetaData
      const data = await getGeneralMetaByRegionData(body)
      if (data.Regioes?.length) {
        const newMetaList = generalMetaData.map((item) => {
          if (item.Equipe === id) {
            return { ...item, expanded: true, regions: data.Regioes }
          }
          return item
        })
        dispatch(setGeneralMetaData(newMetaList))
      }
    } catch (error) {
      toast.error('Erro ao carregar dados.')
    } finally {
      dispatch(setLoadingTableItem(false))
    }
  }

export const getGeneralMetaReport =
  (params: GeneralMetaByTeamRequestBody): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingRankingReport(true))
    toast.info('O download iniciará em instantes.')
    try {
      const data = await getGeneralMetaReportData(params)
      const url = URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      const fileOffice = params.Escritorio ?? 'todos'
      link.setAttribute('download', `geral-meta-${fileOffice}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Arquivo baixado com sucesso!')
    } catch (error) {
      toast.error('Erro ao obter relatório.')
    } finally {
      dispatch(setLoadingRankingReport(false))
    }
  }
